import { LevelSim, SimConstants, Tower, teslaChargeTicks } from "@highmarch/domain";
import { lib } from "./common";
import { Settings } from "../../ui/settings";
import type { TowerView } from "./towerView";

/**
 * The tier-IV models' optional clips (art/hq/README.md "Tier-4 branches"): the Gatling Bastion's `spin` loop while it
 * is shooting, the Tesla Coil's `charge` run on the sim's charge (it ends on the discharge), and the Phoenix Roost's
 * `rebirth` when its phoenix rises with Rebirth's blaze. Each only when the model has the clip; nothing here changes
 * what the sim does.
 */
export const Tier4Clips = {
  /** Seconds after a Gatling's last shot that its barrels keep spinning. */
  SpinHold: 0.6,
  /** The least time left (s) worth starting a Tesla's charge clip for. */
  MinCharge: 0.4,
} as const;

/** Per frame for a tier-IV tower view (after the clips of its shot). */
export function tier4Clips(sim: LevelSim, t: Tower, v: TowerView): void {
  if (t.tier < 4 || v.asc) return;
  const rig = v.model.rig, name = v.model.name;
  if (name.endsWith("_t4_gatling")) {
    const want = v.fireAt < Tier4Clips.SpinHold && t.disabledTicksLeft <= 0 ? "spin" : "idle";
    if (rig.loopClip !== want && rig.has(want)) rig.playLoop(want);
    return;
  }
  if (name.endsWith("_t4_tesla")) {
    if (rig.busy || t.disabledTicksLeft > 0 || t.charge <= 0 || !rig.has("charge")) return;
    const left = (teslaChargeTicks(sim, t) - t.charge) / (SimConstants.TicksPerSecond * Math.max(1, Settings.current.speed));
    if (left < Tier4Clips.MinCharge) return;
    const clip = lib()?.clipsOf(`hq:${name}`).find((c) => c.name === "charge")?.duration ?? 0;
    rig.playOnce("charge", true, null, { timeScale: clip > 0 ? clip / left : 1 });
  }
}

/** PhoenixRisen with Rebirth's blaze (b = 1): the roost's bird bursts into flame and is reborn on its perch. */
export function phoenixRebirth(v: TowerView | undefined): void {
  if (!v || v.asc || !v.model.name.replace(/__[a-z0-9_]+$/, "").endsWith("_t4_phoenix") || !v.model.rig.has("rebirth")) return;
  v.model.rig.playOnce("rebirth", false, null, { solo: true });
}
